import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { categories, getMoviesByCategory, getCategoryName } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'update'>('rating');

  const categoryMovies = useMemo(() => {
    const movies = getMoviesByCategory(slug || 'movie');
    switch (sortBy) {
      case 'year':
        return [...movies].sort((a, b) => b.year - a.year);
      case 'update':
        return [...movies].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
      default:
        return [...movies].sort((a, b) => b.rating - a.rating);
    }
  }, [slug, sortBy]);

  const categoryName = getCategoryName(slug || 'movie');

  const canonicalSlug = slug || 'movie';

  return (
    <>
      <div className="pt-20">
        <div className="px-4 md:px-6">
          <div className="max-w-[1200px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />
          </div>
        </div>

        <main>
          <section className="px-4 md:px-6 py-6">
            <div className="max-w-[1200px] mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {categoryName}
                </h1>

                <div className="flex items-center gap-2 flex-wrap">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`px-4 py-2 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer no-underline ${
                        slug === cat.slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-background-200/70">
                <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
                {[
                  { value: 'rating' as const, label: '评分最高' },
                  { value: 'year' as const, label: '最新上映' },
                  { value: 'update' as const, label: '最近更新' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSortBy(option.value)}
                    className={`px-3 py-1.5 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === option.value
                        ? 'bg-secondary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
                <span className="text-xs text-foreground-400 ml-auto whitespace-nowrap">
                  共 {categoryMovies.length} 部
                </span>
              </div>

              {categoryMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
                  {categoryMovies.map((movie: Movie) => (
                    <MovieCard key={movie.id} movie={movie} size="small" />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-film-line text-2xl" />
                  </div>
                  <p className="text-foreground-500">暂无内容，敬请期待</p>
                </div>
              )}
            </div>
          </section>
        </main>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${categoryName} - 蚂蚁影视`,
            description: `蚂蚁影视${categoryName}频道，高清免费在线观看最新${categoryName}，即点即播不缓冲。`,
            url: `https://www.mayifilm.com/category/${canonicalSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '蚂蚁影视',
              url: 'https://www.mayifilm.com/',
            },
          }),
        }}
      />
    </>
  );
}